import React from 'react';
import ProgramSlideTextDisplayToolbar, {
  DEFAULT_FONT_SIZE_PT_BY_TAB,
  loadProgramSlideTextDisplayPrefs,
  saveProgramSlideTextDisplayPrefs,
  type ProgramSlideTextAlign,
  type ProgramSlideTitleTabId,
} from './ProgramSlideTextDisplayToolbar';

export type { ProgramSlideTitleTabId };

const TITLE_TABS: { id: ProgramSlideTitleTabId; label: string }[] = [
  { id: 'title', label: '타이틀' },
  { id: 'criteria', label: '성취기준' },
  { id: 'learningMemo', label: '학습메모' },
  { id: 'evaluation', label: '평가계획' },
];

const SESSION_EDIT_HINT = '클릭하면 이 화면에서만 수정됩니다. 저장되지 않습니다.';

function TitleTabScrollText({
  text,
  emptyMessage,
  ariaLabel,
  sessionEditable,
  editing,
  draft,
  editRef,
  onStartEdit,
  onDraftChange,
  onCommit,
  onCancel,
}: {
  text: string;
  emptyMessage: string;
  ariaLabel: string;
  sessionEditable?: boolean;
  editing?: boolean;
  draft?: string;
  editRef?: React.RefObject<HTMLTextAreaElement | null>;
  onStartEdit?: () => void;
  onDraftChange?: (next: string) => void;
  onCommit?: () => void;
  onCancel?: () => void;
}) {
  const displayText = (text ?? '').replace(/\r\n/g, '\n');
  const hasText = displayText.trim().length > 0;
  const isEditing = !!(editing && sessionEditable);

  return (
    <div
      className={
        'cp-program-slide-title-scroll-view' +
        (sessionEditable ? ' is-session-editable' : '') +
        (isEditing ? ' is-editing' : '')
      }
      aria-label={ariaLabel}
      role={sessionEditable && !isEditing ? 'button' : undefined}
      tabIndex={sessionEditable && !isEditing ? 0 : undefined}
      title={sessionEditable && !isEditing ? SESSION_EDIT_HINT : undefined}
      onClick={sessionEditable && !isEditing ? onStartEdit : undefined}
      onKeyDown={
        sessionEditable && !isEditing
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onStartEdit?.();
              }
            }
          : undefined
      }
    >
      <div className="cp-program-slide-title-scroll-spacer">
        <div className="cp-program-slide-title-text-block">
          {isEditing ? (
            <textarea
              ref={editRef}
              className="cp-program-slide-title-scroll-edit"
              value={draft ?? ''}
              onChange={(e) => onDraftChange?.(e.target.value)}
              onBlur={onCommit}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancel?.();
                }
              }}
              aria-label={`${ariaLabel} 임시 수정`}
              rows={Math.max(1, (draft ?? '').split('\n').length)}
            />
          ) : hasText ? (
            <div className="cp-program-slide-title-text-pre">{displayText}</div>
          ) : (
            <div className="cp-program-slide-title-text-empty">{emptyMessage}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export interface ProgramSlideTitlePanelProps {
  /** 학습내용 셀 본문(표시용) */
  learningTitle: string;
  /** 단원 메모 성취기준 */
  achievementText: string;
  /** 학습내용 셀 메모 */
  learningMemoNote: string;
  /** 평가 셀 메모 */
  evaluationMemoNote: string;
  /** 글자 크기·정렬 localStorage 키(슬라이드 stash id 등) */
  styleStorageKey?: string;
  className?: string;
  /** 탭 텍스트 클릭 편집. 세션 전용이며 저장하지 않음 */
  titleSessionEditable?: boolean;
  onSessionTitleChange?: (next: string) => void;
  onSessionAchievementChange?: (next: string) => void;
  onSessionLearningMemoChange?: (next: string) => void;
  onSessionEvaluationChange?: (next: string) => void;
}

const ProgramSlideTitlePanel: React.FC<ProgramSlideTitlePanelProps> = ({
  learningTitle,
  achievementText,
  learningMemoNote,
  evaluationMemoNote,
  styleStorageKey,
  className,
  titleSessionEditable = false,
  onSessionTitleChange,
  onSessionAchievementChange,
  onSessionLearningMemoChange,
  onSessionEvaluationChange,
}) => {
  const [activeTab, setActiveTab] = React.useState<ProgramSlideTitleTabId>('title');
  const [editingTab, setEditingTab] = React.useState<ProgramSlideTitleTabId | null>(null);
  const [draft, setDraft] = React.useState('');
  const editRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [fontSizePtByTab, setFontSizePtByTab] = React.useState<Record<ProgramSlideTitleTabId, number>>(() =>
    loadProgramSlideTextDisplayPrefs(styleStorageKey).fontSizePtByTab
  );
  const [textAlign, setTextAlign] = React.useState<ProgramSlideTextAlign>(
    () => loadProgramSlideTextDisplayPrefs(styleStorageKey).textAlign
  );

  const sessionEditable = titleSessionEditable;
  const canEditTitle = sessionEditable && !!onSessionTitleChange;
  const canEditCriteria = sessionEditable && !!onSessionAchievementChange;
  const canEditLearningMemo = sessionEditable && !!onSessionLearningMemoChange;
  const canEditEvaluation = sessionEditable && !!onSessionEvaluationChange;

  React.useEffect(() => {
    const loaded = loadProgramSlideTextDisplayPrefs(styleStorageKey);
    setFontSizePtByTab(loaded.fontSizePtByTab);
    setTextAlign(loaded.textAlign);
  }, [styleStorageKey]);

  const activeFontSizePt = fontSizePtByTab[activeTab] ?? DEFAULT_FONT_SIZE_PT_BY_TAB[activeTab];

  const persistDisplayPrefs = React.useCallback(
    (next: {
      fontSizePtByTab?: Record<ProgramSlideTitleTabId, number>;
      textAlign?: ProgramSlideTextAlign;
    }) => {
      saveProgramSlideTextDisplayPrefs(styleStorageKey, {
        fontSizePtByTab: next.fontSizePtByTab ?? fontSizePtByTab,
        textAlign: next.textAlign ?? textAlign,
      });
    },
    [fontSizePtByTab, styleStorageKey, textAlign]
  );

  const handleFontSizePtChange = React.useCallback(
    (next: number) => {
      setFontSizePtByTab((prev) => {
        const merged = { ...prev, [activeTab]: next };
        persistDisplayPrefs({ fontSizePtByTab: merged });
        return merged;
      });
    },
    [activeTab, persistDisplayPrefs]
  );

  const handleTextAlignChange = React.useCallback(
    (next: ProgramSlideTextAlign) => {
      setTextAlign(next);
      persistDisplayPrefs({ textAlign: next });
    },
    [persistDisplayPrefs]
  );

  const sourceTextForTab = React.useCallback(
    (tab: ProgramSlideTitleTabId): string => {
      if (tab === 'title') return learningTitle ?? '';
      if (tab === 'criteria') return achievementText ?? '';
      if (tab === 'learningMemo') return learningMemoNote ?? '';
      return evaluationMemoNote ?? '';
    },
    [achievementText, evaluationMemoNote, learningMemoNote, learningTitle]
  );

  React.useEffect(() => {
    setEditingTab(null);
  }, [activeTab]);

  React.useEffect(() => {
    if (editingTab == null) return;
    const el = editRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editingTab]);

  React.useLayoutEffect(() => {
    if (editingTab == null) return;
    const el = editRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, editingTab, activeFontSizePt]);

  const commitEdit = React.useCallback(() => {
    if (editingTab == null) return;
    if (editingTab === 'title') {
      if (canEditTitle) onSessionTitleChange?.(draft);
    } else if (editingTab === 'criteria') {
      if (canEditCriteria) onSessionAchievementChange?.(draft);
    } else if (editingTab === 'learningMemo') {
      if (canEditLearningMemo) onSessionLearningMemoChange?.(draft);
    } else if (editingTab === 'evaluation') {
      if (canEditEvaluation) onSessionEvaluationChange?.(draft);
    }
    setEditingTab(null);
  }, [
    canEditCriteria,
    canEditEvaluation,
    canEditLearningMemo,
    canEditTitle,
    draft,
    editingTab,
    onSessionAchievementChange,
    onSessionEvaluationChange,
    onSessionLearningMemoChange,
    onSessionTitleChange,
  ]);

  const cancelEdit = React.useCallback(() => {
    setEditingTab(null);
  }, []);

  const startEdit = React.useCallback(
    (tab: ProgramSlideTitleTabId) => {
      if (tab === 'title' && !canEditTitle) return;
      if (tab === 'criteria' && !canEditCriteria) return;
      if (tab === 'learningMemo' && !canEditLearningMemo) return;
      if (tab === 'evaluation' && !canEditEvaluation) return;
      setDraft(sourceTextForTab(tab));
      setEditingTab(tab);
    },
    [canEditCriteria, canEditEvaluation, canEditLearningMemo, canEditTitle, sourceTextForTab]
  );

  const titleText = (learningTitle ?? '').trim();

  const panelClass =
    'cp-program-slide-title-panel' + (className?.trim() ? ` ${className.trim()}` : '');

  const displayStyle = React.useMemo(
    () =>
      ({
        '--cp-title-tab-font-size': `${activeFontSizePt}pt`,
        '--cp-title-tab-text-align': textAlign,
      }) as React.CSSProperties,
    [activeFontSizePt, textAlign]
  );

  return (
    <div className={panelClass} style={displayStyle}>
      <div className="cp-program-slide-title-tabs-row">
        <ProgramSlideTextDisplayToolbar
          fontSizePt={activeFontSizePt}
          textAlign={textAlign}
          onFontSizePtChange={handleFontSizePtChange}
          onTextAlignChange={handleTextAlignChange}
        />
        <div className="cp-program-slide-title-tabs" role="tablist" aria-label="타이틀 슬라이드 탭">
          {TITLE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={'cp-program-slide-title-tab' + (activeTab === tab.id ? ' is-active' : '')}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="cp-program-slide-title-panel-body"
        role="tabpanel"
        aria-label={
          activeTab === 'title'
            ? '타이틀'
            : activeTab === 'criteria'
              ? '성취기준'
              : activeTab === 'learningMemo'
                ? '학습메모'
                : '평가계획'
        }
      >
        {activeTab === 'title' ? (
          editingTab === 'title' && canEditTitle ? (
            <div className="cp-program-slide-title-heading is-editing">
              <textarea
                ref={editRef}
                className="cp-program-slide-title-heading-inner cp-program-slide-title-heading-edit"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                aria-label="타이틀 임시 수정"
                rows={Math.max(1, draft.split('\n').length)}
              />
            </div>
          ) : titleText ? (
            <div
              className={'cp-program-slide-title-heading' + (canEditTitle ? ' is-session-editable' : '')}
            >
              <div
                className="cp-program-slide-title-heading-inner"
                role={canEditTitle ? 'button' : undefined}
                tabIndex={canEditTitle ? 0 : undefined}
                title={canEditTitle ? SESSION_EDIT_HINT : undefined}
                onClick={canEditTitle ? () => startEdit('title') : undefined}
                onKeyDown={
                  canEditTitle
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          startEdit('title');
                        }
                      }
                    : undefined
                }
              >
                {titleText}
              </div>
            </div>
          ) : (
            <div
              className="cp-program-slide-title-scroll-view"
              aria-label="타이틀"
              role={canEditTitle ? 'button' : undefined}
              tabIndex={canEditTitle ? 0 : undefined}
              title={canEditTitle ? SESSION_EDIT_HINT : undefined}
              onClick={canEditTitle ? () => startEdit('title') : undefined}
              onKeyDown={
                canEditTitle
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startEdit('title');
                      }
                    }
                  : undefined
              }
            >
              <div className="cp-program-slide-title-scroll-spacer">
                <div className="cp-program-slide-title-text-block">
                  <div className="cp-program-slide-title-text-empty">
                    {canEditTitle ? '클릭해서 타이틀을 임시로 적어 보세요.' : '등록된 학습내용이 없습니다.'}
                  </div>
                </div>
              </div>
            </div>
          )
        ) : null}
        {activeTab === 'criteria' ? (
          <TitleTabScrollText
            text={achievementText ?? ''}
            emptyMessage={
              canEditCriteria
                ? '클릭해서 성취기준을 임시로 적어 보세요.'
                : '등록된 성취기준이 없습니다.'
            }
            ariaLabel="단원 메모 성취기준 본문"
            sessionEditable={canEditCriteria}
            editing={editingTab === 'criteria'}
            draft={draft}
            editRef={editRef}
            onStartEdit={() => startEdit('criteria')}
            onDraftChange={setDraft}
            onCommit={commitEdit}
            onCancel={cancelEdit}
          />
        ) : null}
        {activeTab === 'learningMemo' ? (
          <TitleTabScrollText
            text={learningMemoNote ?? ''}
            emptyMessage={
              canEditLearningMemo
                ? '클릭해서 학습메모를 임시로 적어 보세요.'
                : '등록된 학습메모가 없습니다.'
            }
            ariaLabel="학습내용 메모"
            sessionEditable={canEditLearningMemo}
            editing={editingTab === 'learningMemo'}
            draft={draft}
            editRef={editRef}
            onStartEdit={() => startEdit('learningMemo')}
            onDraftChange={setDraft}
            onCommit={commitEdit}
            onCancel={cancelEdit}
          />
        ) : null}
        {activeTab === 'evaluation' ? (
          <TitleTabScrollText
            text={evaluationMemoNote ?? ''}
            emptyMessage={
              canEditEvaluation
                ? '클릭해서 평가계획을 임시로 적어 보세요.'
                : '등록된 평가 메모가 없습니다.'
            }
            ariaLabel="평가 메모"
            sessionEditable={canEditEvaluation}
            editing={editingTab === 'evaluation'}
            draft={draft}
            editRef={editRef}
            onStartEdit={() => startEdit('evaluation')}
            onDraftChange={setDraft}
            onCommit={commitEdit}
            onCancel={cancelEdit}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ProgramSlideTitlePanel;
